import { Link } from 'react-router';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import type { NavItem } from '@/layouts/nav-sections';
import { resolveNavItem, useShortcuts } from '@/modules/shortcuts/shortcuts';
import { MODULE_ICONS } from './module-icons';

/** The dashboard's entry point into personal shortcuts — a preview when the
 *  caller has some, a plain invitation to go add one when they don't. Either
 *  way this is where anyone finds `/shortcuts` for the first time, since
 *  nothing about the feature shows up in the sidebar until it's used once. */
export function ShortcutsWidget() {
  const { t } = useI18n();
  const { can } = useAuth();
  const { shortcuts } = useShortcuts();

  const items = shortcuts
    .map((to) => resolveNavItem(to))
    .filter((item): item is NavItem => item !== undefined)
    .filter((item) => item.permission === null || can(item.permission));

  return (
    <div className="dashboard-category">
      <div className="dashboard-shortcuts-header">
        <p className="dashboard-category-label">{t('shortcuts.title')}</p>
        <Link to="/shortcuts" className="dashboard-shortcuts-manage">
          {t('shortcuts.manage')}
        </Link>
      </div>

      {items.length === 0 ? (
        <Link to="/shortcuts" className="dashboard-shortcuts-empty">
          {t('shortcuts.emptyCta')}
        </Link>
      ) : (
        <div className="dashboard-tiles">
          {items.map((item) => {
            const Icon = MODULE_ICONS[item.to];
            return (
              <Link to={item.to} className="dashboard-tile" key={item.to}>
                <span className="dashboard-tile-icon">{Icon && <Icon />}</span>
                <span className="dashboard-tile-label">{t(item.labelKey)}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
